import axios from 'axios';

// Use relative URL when in development to work with proxy
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || 'https://api.prepstop.com')
  : process.env.REACT_APP_API_URL; // Empty string means use proxy in development

export interface PatientDashboardData {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  display_status: string;
  statusMessage: string;
  appointments?: {
    last_visit?: {
      date: string;
      type: string;
    };
    upcoming_hiv_test?: {
      date: string;
      status: string;
    };
    upcoming_telehealth?: {
      date: string;
      status: string;
    };
  };
  progress: {
    registration: boolean;
    intake_block_b_completed: boolean;
    hiv_test_completed: boolean;
    insurance_verified: boolean;
    telehealth_completed: boolean;
    prescription_issued: boolean;
    medication_active: boolean;
  };
  medication: {
    prep_type: string;
    medication_name: string | null;
    last_dose_date: string | null;
    next_refill_date: string | null;
  } | null;
  actions: Array<{
    label: string;
    action: string;
    primary: boolean;
  }>;
  incompleteBlockB?: {
    voucherId: string;
    voucherUrl: string;
  } | null;
  currentCase: {
    id: string;
    status: string;
    createdAt: string;
  } | null;
  currentHIVTest?: {
    id: number;
    test_date: string;
    test_result: 'NEGATIVE' | 'POSITIVE' | 'INCONCLUSIVE';
    test_type: string | null;
    test_location: string | null;
    status: 'PENDING_REVIEW' | 'VERIFIED' | 'REJECTED' | 'NEEDS_RETEST';
  } | null;
  insurance_provider?: string | null;
  insurance_policy_number?: string | null;
  insurance_group_number?: string | null;
  insurance_status?: string | null;
  insurance_documents?: Array<{
    id: number;
    document_type: 'FRONT' | 'BACK' | 'OTHER';
    filename: string;
    file_size: number;
    mime_type: string;
    uploaded_at: string;
    status: string;
    url: string;
  }>;
  email_notifications?: boolean;
  sms_notifications?: boolean;
  phone_notifications?: boolean;
  telehealth_completed?: boolean;
  prescription_issued?: boolean;
  medication_name?: string | null;
  prep_type?: string | null;
  calendlyEventUri?: string | null;
  updatedAt?: string;
  createdAt: string;
  market: string;
}

class PatientDashboardService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      withCredentials: true,
    };
  }

  /**
   * Get current patient's dashboard data
   */
  async getMyDashboardData(): Promise<PatientDashboardData> {
    const response = await axios.get(
      `${API_BASE_URL}/api/dashboard/patient/my-data`,
      this.getAuthHeaders()
    );
    return response.data;
  }

  /**
   * Continue intake by creating MDI voucher for Block B
   */
  async continueIntake(): Promise<{ voucher: { url: string; id: string } }> {
    console.log('📤 Dashboard Service: Calling continue-intake API...');
    const response = await axios.post(
      `${API_BASE_URL}/patient/continue-intake`,
      {},
      this.getAuthHeaders()
    );
    console.log('📥 Dashboard Service: Response received');
    return response.data;
  }

  /**
   * Update patient insurance status (for pharmacy partners)
   */
  async updateInsuranceStatus(patientId: string, status: string): Promise<void> {
    const response = await axios.patch(
      `${API_BASE_URL}/api/patient/${patientId}/insurance-status`,
      { insurance_status: status },
      this.getAuthHeaders()
    );
    return response.data;
  }
}

export const patientDashboardService = new PatientDashboardService();
export default patientDashboardService;
